import { HvlBaseModel, HvlPage, HvlQueryModel, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { LookupModel } from 'infrastructure-shared';

export class BaseService<BM extends HvlBaseModel, QM extends HvlQueryModel> {
    urlPattern: string;
    endpoint: string;
    httpClient: HttpClient;
    bm: new () => BM;
    qm: new () => QM;

    constructor(
        urlPattern: string,
        httpClient: HttpClient,
        cTypeBm: new () => BM,
        cTypeQm: new () => QM,
        endpoint: string,
    ) {
        this.bm = cTypeBm;
        this.qm = cTypeQm;
        this.urlPattern = urlPattern;
        this.httpClient = httpClient;
        this.endpoint = endpoint;
    }

    public update(model: BM): Observable<BM> {
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<BM>>(this.endpoint + this.urlPattern, converter.serialize(model, this.bm))
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, this.bm);
                }),
            );
    }

    public queryList(queryModel: QM): Observable<Array<BM>> {
        const pattern = this.urlPattern + '/query';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<BM>>>(this.endpoint + pattern, converter.serialize(queryModel, this.qm))
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], this.bm);
                }),
            );
    }

    public queryListByLocale(queryModel: QM, locale: string): Observable<Array<BM>> {
        const pattern = this.urlPattern + '/query/byLocale/' + locale;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<BM>>>(this.endpoint + pattern, converter.serialize(queryModel, this.qm))
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], this.bm);
                }),
            );
    }

    public save(model: BM): Observable<BM> {
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<BM>>(this.endpoint + this.urlPattern, converter.serialize(model, this.bm))
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, this.bm);
                }),
            );
    }

    public getList(): Observable<Array<BM>> {
        const serviceUrl = this.endpoint + this.urlPattern; // Full path
        return this.httpClient.get<HvlResponse<Array<BM>>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], this.bm);
            }),
        );
    }

    public deleteById(id: number): Observable<any> {
        const pattern = this.urlPattern + '/' + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public deleteByUuid(uuid: string): Observable<any> {
        const pattern = this.urlPattern + '/' + uuid;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public getById(id: number): Observable<BM> {
        const pattern = this.urlPattern + '/' + id;
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<BM>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, this.bm);
            }),
        );
    }

    public queryPage(pagination: QM): Observable<HvlPage<Array<BM>>> {
        const pattern = this.urlPattern + '/page';
        return this.httpClient.post<HvlResponse<HvlPage<BM[]>>>(this.endpoint + pattern, pagination).pipe(
            map((hvlpage) => {
                const converter = new JsonConvert();
                if (hvlpage.body?.totalElements === 0) {
                    return new HvlPage<Array<BM>>(
                        [],
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }
                const model = converter.deserializeArray(hvlpage.body.data, this.bm);
                return new HvlPage<Array<BM>>(
                    model as BM[],
                    hvlpage.body.totalElements,
                    hvlpage.body.totalPages,
                    hvlpage.errorDetail,
                );
            }),
        );
    }

    public lookupQuery(queryModel: QM): Observable<Array<LookupModel>> {
        const pattern = this.urlPattern + '/lookupQuery';
        const url = this.endpoint + pattern;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(url, converter.serialize(queryModel, this.qm))
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data?.body || [], LookupModel);
                }),
            );
    }
}
